import React, { useMemo, useState, useEffect } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// components
import { GenericTableHeader } from "@surgia/ui";
import Button from "@surgia/ui/components/button";
import TableControls from "@surgia/ui/containers/table/table-controls";
import GenericModal from "@surgia/ui/components/genericModal";
import HeaderLayout from "@surgia/ui/layouts/Header";
import ListboxDropdown from "@surgia/ui/components/ListboxDropdown";
//containers
import UsersTable from "../../containers/tables/users-table";
import { InviteUserForm } from "../../containers/forms/inviteUserForm";
// queries
import { usersQueries } from "@surgia/api/src/queryStore/shared/users";
import { useOutletContext } from "react-router-dom";
// constants
const ITEMS_PER_PAGE = 8;
const User_Permissions = JSON.parse(
  localStorage.getItem("permissions") || "[]"
);
import { permissions } from "../../permissions";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
//icons
import addIcon from "@surgia/ui/icons/actions/add.svg";
import inviteIcon from "@surgia/ui/icons/users/inviite.svg";
//hooks
import { useSendInvitation } from "../../hooks/useSendInvitation";

interface OutletContextType {
  branchesDropdown: any[];
  selectedBranch: string | null;
  setSelectedBranch: (branch: string | null) => void;
  isbranchesDropDownLoading: boolean;
}

const branchesIds = JSON.parse(localStorage.getItem("branches") || "[]");
const allBranchesIds = branchesIds.join(",");

export const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
  branches,
  userGroups,
  cities,
  showCities = false,
  isUserGroupsLoading = false,
  isBranchesLoading = false,
  isCitiesLoading = false,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (filters: any) => void;
  branches: any[];
  cities?: any[];
  userGroups?: any[];
  showCities?: boolean;
  isUserGroupsLoading?: boolean;
  isBranchesLoading?: boolean;
  isCitiesLoading?: boolean;
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const { t } = useTranslation();

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({ city_id: "", user_group_id: "", branch_ids: "" });
    setFilters({ city_id: "", user_group_id: "", branch_ids: "" });
  };

  // Find selected user group
  const selectedUserGroup = userGroups?.find(
    (group) => String(group.id) === localFilters.user_group_id
  );

  // Find selected city
  const selectedCity = cities?.find(
    (city) => city?.id?.toString() === localFilters.city_id
  );

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      additionalTitleStyles="!mb-0 text-[16px]"
      additionalSubtitleStyles="!text-[14px]"
      additionalStyles="!overflow-visible !w-[500px]"
      footer={
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={handleClear}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Cities Filter - ListboxDropdown */}
        {showCities && ((cities && cities.length > 0) || isCitiesLoading) && (
          <ListboxDropdown
            options={
              cities?.map((city) => ({ id: city.id, name: city.name })) || []
            }
            value={
              selectedCity
                ? { id: selectedCity.id, name: selectedCity.name }
                : null
            }
            onChange={(city) =>
              setLocalFilters({
                ...localFilters,
                city_id: city && !Array.isArray(city) ? String(city.id) : "",
              })
            }
            label="City"
            placeholder="Select City"
            includeSelectOption={true}
            selectOptionText="Select City"
            loading={isCitiesLoading}
            loadingText="Loading cities..."
            buttonClassName="border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300 bg-white text-left"
          />
        )}

        {/* Branch */}
        {((branches && branches.length > 0) || isBranchesLoading) && (
          <ListboxDropdown
            options={
              branches?.map((branch) => ({
                id: branch.id,
                name: branch.name,
              })) || []
            }
            value={
              branches?.find(
                (branch) => String(branch.id) === localFilters.branch_ids
              ) || null
            }
            onChange={(selectedBranch) =>
              setLocalFilters({
                ...localFilters,
                branch_ids:
                  selectedBranch && !Array.isArray(selectedBranch)
                    ? String(selectedBranch.id)
                    : "",
              })
            }
            label="Branch"
            placeholder="Select Branch"
            loading={isBranchesLoading}
            loadingText="Loading branches..."
          />
        )}
        {/* Role */}
        {((userGroups && userGroups.length > 0) || isUserGroupsLoading) && (
          <ListboxDropdown
            options={
              userGroups?.map((role) => ({
                id: role.id,
                name: role.name,
              })) || []
            }
            value={
              selectedUserGroup
                ? { id: selectedUserGroup.id, name: selectedUserGroup.name }
                : null
            }
            onChange={(group) =>
              setLocalFilters({
                ...localFilters,
                user_group_id:
                  group && !Array.isArray(group) ? String(group.id) : "",
              })
            }
            label="Role"
            placeholder="Select Role"
            includeSelectOption={true}
            selectOptionText="Select Role"
            loading={isUserGroupsLoading}
            loadingText="Loading roles..."
            buttonClassName="border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300 bg-white text-left"
          />
        )}
      </div>
    </GenericModal>
  );
};

// Main Users Component
const Users: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const { sendInvitation } = useSendInvitation();
  const TARGET_COMPONENT_ID =
    import.meta.env.VITE_VITEAUTH_URL?.includes("test") ||
    import.meta.env.VITE_VITEAUTH_URL?.includes("develop")
      ? 8
      : 6;
  const authState = useSelector((state: RootState) => state.auth);
  const tenant_id = authState.globalState.tenant_id;

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("");
  const [search, setSearch] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [filters, setFilters] = useState({ city_id: "", user_group_id: "" });
  const {
    branchesDropdown,
    selectedBranch,
    setSelectedBranch,
    isbranchesDropDownLoading,
  } = useOutletContext<OutletContextType>();

  const statusTabs = [
    { label: "All", value: "" },
    { label: i18n.t("shared.tableToolbar.active"), value: "ACTIVE" },
    { label: i18n.t("shared.tableToolbar.pending"), value: "PENDING" },
  ];

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "userId", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  // Fetch users data with sorting and city filter
  const { data: usersResponse, isLoading: isUsersLoading } = useQuery({
    ...usersQueries.queries.getUsers({
      component_id:
        import.meta.env.VITE_KEYCLOAK_ISSUER?.includes("test") ||
        import.meta.env.VITE_KEYCLOAK_ISSUER?.includes("develop")
          ? 8
          : 6,
      name: search,
      tenant_id: tenant_id!,
      branches_ids: selectedBranch ?? "",
      id: "",
      //cleanup is required for the status
      profile_status: status !== "" && status ? status : "",
      size: ITEMS_PER_PAGE,
      page: currentPage,
      city_id: filters.city_id || "",
      userGroup_id: filters.user_group_id || "",
      // Add sorting parameters using sortKey
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    }),
    placeholderData: keepPreviousData,
    select: (data) => {
      return {
        total: data.total,
        data: data.data.map((user: any) => ({
          ...user,
          id: user.user_id,
          name: user.full_name,
          details: {
            email: user.email,
            phone: user.mobile,
          },
          role: user.user_groups,
          status: user.status,
          createdAt: user.creation_date,
          lastLogin: user.last_login,
          branches: user.user_branches,
          actions: user.user_id,
        })),
      };
    },
  });

  // Add the userGroups query
  const { data: userGroupsResponse, isLoading: isUserGroupsLoading } = useQuery(
    {
      ...usersQueries.queries.getUserGroups({
        component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
        tenant_id: tenant_id!,
        target_component_id: TARGET_COMPONENT_ID,
        target_tenant_id: tenant_id!,
      }),
    }
  );

  // Extract userGroups from the response
  const userGroups = userGroupsResponse?.data || [];

  // Computed values
  const totalCount = useMemo(() => {
    return usersResponse?.total || 0;
  }, [usersResponse?.total]);

  const onSubmit = (data: any) => {
    sendInvitation({
      tenant_id: tenant_id!,
      email: data.email,
      user_group_ids_list: data.role,
      branch_ids_list: data.branch,
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
    });

    setShowInviteModal(false);
  };
  useEffect(() => {
    setCurrentPage(0);
  }, [search, filters]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  // Access the global branch context

  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      <HeaderLayout
        title="Users"
        subtitle="Manage and monitor all users"
        additionalStyles="justify-between"
        branches={branchesDropdown}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        allUserBranches={allBranchesIds}
        showBranchesDropDown={authState?.globalState?.user_branches?.includes(
          ","
        )}
        showAllBranches={true}
        isBranchesLoading={isbranchesDropDownLoading}
      >
        <Button
          variant="primary"
          startIcon={addIcon}
          onClick={() => setShowInviteModal(true)}
          disabled={
            !User_Permissions.includes(
              permissions.INVITATION_CREATE,
              permissions.USER_CREATE,
              permissions.VIEW_USER_GROUPS
            )
          }
        >
          Add User
        </Button>
      </HeaderLayout>
      <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
        <GenericTableHeader
          title="Users"
          totalCount={totalCount}
          status={status}
          singularLabel="user"
          pluralLabel="users"
        />

        <TableControls
          status={status}
          setStatus={setStatus}
          search={search}
          setSearch={setSearch}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          statusTabs={statusTabs}
          showFilter={true}
        />

        {/* Users Table Section */}
        <UsersTable
          data={usersResponse?.data || []}
          currentPage={currentPage}
          totalPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
          onPageChange={setCurrentPage}
          loading={isUsersLoading}
          showSelectedBranchColumn={selectedBranch?.includes(",")}
          sortState={sortState}
          onSort={handleSort}
        />
      </div>

      {/* Invite User Modal */}
      <GenericModal
        title="Invite new user"
        subtitle="Add a new user to your tenant account here and assign their role."
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        titleIcon={<img src={inviteIcon} alt="Invite" className="w-10 h-10" />}
        additionalTitleStyles="!mb-0 text-[16px]"
        additionalSubtitleStyles="!text-[14px]"
        additionalStyles="!overflow-visible"
        footer={<></>}
      >
        <InviteUserForm
          onSuccess={onSubmit}
          onCancel={() => setShowInviteModal(false)}
          userGroups={userGroups}
          isLoadingUserGroups={isUserGroupsLoading}
          branches={branchesDropdown}
          branchesLoading={isbranchesDropDownLoading}
        />
      </GenericModal>
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
          branches={[]}
          userGroups={userGroups}
          isUserGroupsLoading={isUserGroupsLoading}
        />
      )}
    </div>
  );
};

export default Users;
